import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';

/**
 * Manual de Usuario navegable dentro de la plataforma.
 * Espejo del PDF generado por backend/generate_manual_pdf.py.
 * Incluye TOC lateral, búsqueda, anclas a cada sección y descarga del PDF.
 */
const API = process.env.REACT_APP_BACKEND_URL || '';

const SECTIONS = [
  { id: 'intro',      n: '01', title: 'Introducción' },
  { id: 'login',      n: '02', title: 'Acceso a la plataforma',     img: '01_login.jpeg' },
  { id: 'dashboard',  n: '03', title: 'Dashboard',                  img: '02_dashboard.jpeg' },
  { id: 'fichajes',   n: '04', title: 'Fichajes',                   img: '03_fichajes.jpeg' },
  { id: 'empleados',  n: '05', title: 'Empleados',                  img: '04_empleados.jpeg' },
  { id: 'analisis',   n: '06', title: 'Análisis',                   img: '05_analisis.jpeg' },
  { id: 'contratos',  n: '07', title: 'Contratos',                  img: '06_contratos.jpeg' },
  { id: 'turnos',     n: '08', title: 'Turnos por centro',          img: '06b_turnos.jpeg' },
  { id: 'sync',       n: '09', title: 'Sincronización',             img: '07_sync.jpeg' },
  { id: 'admin',      n: '10', title: 'Gestión de accesos',         img: '08_admin.jpeg' },
  { id: 'config',     n: '11', title: 'Configuración',              img: '09_config.jpeg' },
  { id: 'terminales', n: '12', title: 'Terminales de fichaje',      img: '09b_terminales.jpeg' },
  { id: 'soporte',    n: '13', title: 'Soporte y feedback' },
];

const Img = ({ src, caption }) => (
  <figure style={{ margin: '14px 0' }}>
    <img
      src={`${API}/api/manual/screenshots/${src}`}
      alt={caption}
      loading="lazy"
      style={{ width: '100%', borderRadius: '10px', border: '1px solid #334155', display: 'block' }}
    />
    <figcaption style={{ color: '#64748b', fontSize: '12px', marginTop: '6px', fontStyle: 'italic', textAlign: 'center' }}>
      Figura — {caption}
    </figcaption>
  </figure>
);

const InfoBox = ({ children, variant = 'info' }) => {
  const palette = variant === 'warning'
    ? { bg: '#451a03', border: '#f59e0b', label: '!' }
    : { bg: '#0c4a6e', border: '#38bdf8', label: 'i' };
  return (
    <aside
      style={{
        background: palette.bg,
        borderLeft: `3px solid ${palette.border}`,
        borderRadius: '8px',
        padding: '12px 14px 12px 18px',
        margin: '14px 0',
        color: '#e2e8f0',
        fontSize: '13px',
        lineHeight: 1.55,
        display: 'flex',
        gap: '10px',
      }}
    >
      <span style={{ color: palette.border, fontWeight: 700, fontSize: '15px', lineHeight: '20px' }}>{palette.label}</span>
      <div>{children}</div>
    </aside>
  );
};

const H1 = ({ n, children, id }) => (
  <h1 id={id} style={{
    fontFamily: 'Space Grotesk', color: '#f1f5f9', fontSize: '28px',
    fontWeight: 700, margin: '0 0 6px', scrollMarginTop: '100px',
    display: 'flex', alignItems: 'baseline', gap: '12px',
  }}>
    <span style={{ color: '#60a5fa', fontSize: '15px', fontFamily: 'IBM Plex Mono' }}>{n}</span>
    {children}
  </h1>
);

const H2 = ({ children }) => (
  <h2 style={{
    color: '#3b82f6', fontSize: '16px', fontWeight: 600,
    margin: '20px 0 8px', fontFamily: 'Space Grotesk',
  }}>{children}</h2>
);

const P = ({ children }) => (
  <p style={{ color: '#cbd5e1', fontSize: '14px', lineHeight: 1.65, margin: '0 0 10px' }}>{children}</p>
);

const Ul = ({ items }) => (
  <ul style={{ color: '#cbd5e1', fontSize: '14px', lineHeight: 1.65, paddingLeft: '20px', margin: '0 0 12px' }}>
    {items.map((it, i) => <li key={i} dangerouslySetInnerHTML={{ __html: it }} style={{ marginBottom: '4px' }} />)}
  </ul>
);

export default function AyudaPage() {
  const { user } = useAuth();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    if (!query) return SECTIONS;
    const q = query.toLowerCase();
    return SECTIONS.filter(s => s.title.toLowerCase().includes(q) || s.n.includes(q));
  }, [query]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      style={{ padding: '24px 32px', maxWidth: '1400px' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '6px' }}>
        <div>
          <h1 style={{ fontFamily: 'Space Grotesk', color: '#f1f5f9', fontSize: '32px', fontWeight: 700, margin: 0 }}>
            Manual de Usuario
          </h1>
          <p style={{ color: '#64748b', fontSize: '14px', margin: '4px 0 0' }}>
            Guía completa de SmartClockIn · Versión actualizada con capturas en vivo
          </p>
        </div>
        <a
          data-testid="manual-download-pdf"
          href={`${API}/api/manual/download`}
          target="_blank"
          rel="noreferrer"
          style={{
            background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
            color: '#fff', padding: '12px 22px', borderRadius: '10px',
            textDecoration: 'none', fontWeight: 600, fontSize: '14px',
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            boxShadow: '0 4px 12px rgba(59,130,246,0.30)',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          Descargar PDF
        </a>
      </div>

      <hr style={{ border: 0, borderTop: '1px solid #1e293b', margin: '20px 0 24px' }} />

      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '32px' }}>
        {/* TOC sidebar */}
        <aside style={{ position: 'sticky', top: '80px', alignSelf: 'flex-start', maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' }}>
          <Input
            data-testid="manual-search"
            placeholder="Buscar sección…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            style={{ background: '#0f172a', border: '1px solid #334155', color: '#f1f5f9', marginBottom: '14px' }}
          />
          <nav style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {filtered.map(s => (
              <a
                key={s.id}
                href={`#${s.id}`}
                data-testid={`manual-toc-${s.id}`}
                style={{
                  color: '#94a3b8', textDecoration: 'none', fontSize: '13px',
                  padding: '8px 10px', borderRadius: '6px', display: 'flex', gap: '8px',
                  transition: 'background 150ms',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#1e293b'; e.currentTarget.style.color = '#f1f5f9'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#94a3b8'; }}
              >
                <span style={{ color: '#3b82f6', fontFamily: 'IBM Plex Mono', fontSize: '11px', minWidth: '20px' }}>{s.n}</span>
                <span>{s.title}</span>
              </a>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <main style={{ minWidth: 0 }}>
          <section style={{ marginBottom: '40px' }}>
            <H1 n="01" id="intro">Introducción</H1>
            <P><b>SmartClockIn</b> es la plataforma de Valoriza para el control inteligente de jornada laboral. Procesa los fichajes brutos de los terminales (vía SFTP) y los registros operativos (vía AWS RDS), aplica reglas de turno, detecta incidencias y produce KPIs accionables.</P>
            <H2>Roles del sistema</H2>
            <Ul items={[
              '<b>admin</b>: acceso completo a empleados, fichajes, contratos, sync, configuración.',
              '<b>manager</b>: lectura/edición sobre sus centros asignados (Row-Level Security).',
              '<b>viewer</b>: solo lectura sobre sus centros permitidos.',
            ]} />
            <P>Usuario actual: <b style={{ color: '#22c55e' }}>{user?.email}</b> · rol <b>{user?.role}</b>.</P>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <H1 n="02" id="login">Acceso a la plataforma</H1>
            <P>SmartClockIn ofrece dos métodos de acceso:</P>
            <Ul items={[
              '<b>SSO Microsoft (Entra ID)</b>: recomendado. Usa tu cuenta corporativa @valorizasm.com.',
              '<b>Acceso de emergencia</b>: email + contraseña, para usuarios no integrados en Entra.',
            ]} />
            <Img src="01_login.jpeg" caption="Pantalla de inicio de sesión." />
          </section>

          <section style={{ marginBottom: '40px' }}>
            <H1 n="03" id="dashboard">Dashboard</H1>
            <P>El Dashboard reúne los KPIs operacionales del día: fichajes correctos, auto-cerrados, horas totales, distribución por turno, tendencia 14 días y top centros.</P>
            <Img src="02_dashboard.jpeg" caption="Dashboard principal con KPIs del día." />
          </section>

          <section style={{ marginBottom: '40px' }}>
            <H1 n="04" id="fichajes">Fichajes</H1>
            <P>El módulo Fichajes muestra todos los registros de jornada calculados. Filtra, exporta, genera reportes IA y <b>rectifica</b> manualmente registros con incidencias.</P>
            <Img src="03_fichajes.jpeg" caption="Listado de fichajes con filtros y acciones." />
            <H2>Columna «Dispositivo»</H2>
            <P>Muestra el número de serie del terminal. Si el admin configuró un alias (ver <a href="#terminales" style={{ color: '#60a5fa' }}>Terminales</a>), aparece el nombre legible con el serial debajo.</P>
            <H2>Rectificar un fichaje</H2>
            <Ul items={[
              'Acepta formato <b>HH:MM</b> o <b>HH:MM:SS</b>.',
              'Combina la hora con la fecha del fichaje y recalcula horas trabajadas.',
              'Marca el fichaje como <b>«Correcto (rectificado)»</b>.',
              'Persiste en log de auditoría: usuario, fecha, valor anterior, valor nuevo, motivo.',
            ]} />
            <Img src="03b_rectificar.jpeg" caption="Diálogo de rectificación de fichaje." />
            <InfoBox>
              <b>Buena práctica:</b> indica siempre el motivo (p. ej. «Incidencia con dispositivo»). Facilita auditorías futuras.
            </InfoBox>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <H1 n="05" id="empleados">Empleados</H1>
            <P>Censo activo sincronizado desde el SFTP. Cada tarjeta resume puesto, organización, centro y última sincronización del CSV.</P>
            <Img src="04_empleados.jpeg" caption="Tarjetas de empleados con buscador y filtros." />
          </section>

          <section style={{ marginBottom: '40px' }}>
            <H1 n="06" id="analisis">Análisis</H1>
            <P>Vista de tendencias agregadas: comparativa entre centros, evolución mensual, distribución por turnos. Recomendada para reuniones de seguimiento.</P>
            <Img src="05_analisis.jpeg" caption="Vista de Análisis con KPIs avanzados." />
          </section>

          <section style={{ marginBottom: '40px' }}>
            <H1 n="07" id="contratos">Contratos</H1>
            <P>Define horas comprometidas y periodos por pliego/centro. El motor de KPI usa estos contratos para calcular cumplimiento.</P>
            <Img src="06_contratos.jpeg" caption="Listado y gestión de contratos." />
          </section>

          <section style={{ marginBottom: '40px' }}>
            <H1 n="08" id="turnos">Turnos por centro</H1>
            <P>Define plantillas de turnos específicas por centro de trabajo. Sustituyen los rangos por defecto del sistema.</P>
            <Img src="06b_turnos.jpeg" caption="Plantillas de turnos por centro." />
            <InfoBox>
              <b>Row-Level Security:</b> los managers solo editan plantillas de sus centros; los admins editan todas.
            </InfoBox>
            <InfoBox variant="warning">
              <b>Tras crear/editar plantillas:</b> lanza <i>«Reclasificar fichajes existentes»</i> (Configuración → Motor KPI) para aplicar al histórico.
            </InfoBox>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <H1 n="09" id="sync">Sincronización</H1>
            <P>Pipeline de ingesta: SFTP → RDS → cálculo de KPIs → MongoDB.</P>
            <Img src="07_sync.jpeg" caption="Panel de sincronización manual y log en vivo." />
            <InfoBox>
              <b>Auto-chunking:</b> rangos &gt; 7 días se dividen automáticamente en bloques de 7d con reintentos para evitar timeouts de RDS.
            </InfoBox>
            <H2>Detectar y rellenar días sin datos</H2>
            <P>Si alguna sincronización pasada falló y quedaron huecos, este panel los detecta y los sincroniza solo a ellos.</P>
            <Img src="07b_heal_gaps.jpeg" caption="Detección y relleno automático de huecos." />
            <H2>Auto-curación nocturna</H2>
            <P>Cada noche el sistema ejecuta heal-gaps sobre los últimos N días sin intervención humana. Configurable en Configuración → Sistema (hora y ventana).</P>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <H1 n="10" id="admin">Gestión de accesos</H1>
            <P>Asignación de roles y centros permitidos. Solo accesible para administradores.</P>
            <Img src="08_admin.jpeg" caption="Panel de gestión de accesos y roles." />
            <InfoBox variant="warning">
              Los nombres en <b>allowed_centers</b> deben coincidir <b>exactamente</b> con los nombres reales en RDS. Usa el selector que ofrece el panel para garantizarlo.
            </InfoBox>
          </section>

          <section style={{ marginBottom: '40px' }}>
            <H1 n="11" id="config">Configuración</H1>
            <P>Cinco pestañas: Azure SSO, SFTP, Motor KPI, Terminales, Sistema.</P>
            <Img src="09_config.jpeg" caption="Pantalla principal de Configuración." />
            <H2>Reclasificar fichajes existentes (Motor KPI)</H2>
            <P>Re-aplica la configuración actual a todos los fichajes guardados. No accede a RDS. Úsala tras cambiar rangos, tolerancias, plantillas de turno o reglas de auto-cierre.</P>
            <Img src="09c_motor_kpi.jpeg" caption="Pestaña Motor KPI con botón de reclasificación." />
            <H2>Auto-curación nocturna (Sistema)</H2>
            <P>Activa o desactiva el cron nocturno, ajusta hora (default 04:00 Madrid) y ventana (default 14 días).</P>
            <Img src="09d_sistema.jpeg" caption="Pestaña Sistema con auto-curación nocturna." />
          </section>

          <section style={{ marginBottom: '40px' }}>
            <H1 n="12" id="terminales">Terminales de fichaje</H1>
            <P>Asocia a cada número de serie de terminal un <b>alias legible</b> (p. ej. «Entrada principal») y una ubicación opcional. Los alias aparecen automáticamente en la columna Dispositivo de Fichajes y en los exports.</P>
            <Img src="09b_terminales.jpeg" caption="Panel Terminales con detección automática y aliases." />
            <Ul items={[
              'Detecta automáticamente terminales con actividad en los últimos 60 días.',
              'Para cada uno: nº serie, alias actual, ubicación, fichajes 60d, último uso.',
              '<b>Etiquetar/Editar/Eliminar</b> (solo admins). Cambios inmediatos, sin resincronización.',
            ]} />
          </section>

          <section style={{ marginBottom: '40px' }}>
            <H1 n="13" id="soporte">Soporte y feedback</H1>
            <P>En cada página, esquina inferior derecha, encontrarás un icono de mensaje flotante. Úsalo para reportar bugs, sugerencias o dudas. Llega directamente al equipo de Innovation de Valoriza.</P>
            <InfoBox>
              <b>Tiempo de respuesta:</b> incidencias críticas &lt; 4h hábiles. Mejoras se priorizan en el roadmap mensual.
            </InfoBox>
          </section>
        </main>
      </div>
    </motion.div>
  );
}
